import classnames from 'classnames';
import Image from 'next/image';
import ResizedCdnImage from '@/components/global/images/resized-cdn-image';
import { useParams } from 'next/navigation';
import React, { useEffect, useState } from 'react';

import { doTranslation } from '@/app/i18n/client';
import { FormError } from '@/utils/types';

import styles from '@/components/global/forms/input.module.scss';
import eye from '@/public/assets/images/global/purchase/purchase-login/eye.svg';
import info from '@/public/assets/images/global/purchase/purchase-login/info.svg';

import dynamic from 'next/dynamic';
const Tooltip = dynamic(() => import('@/components/global/tooltip/tooltip'), {
  ssr: false
});

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  cardType?: string;
  dark?: boolean;
  error?: FormError;
  errorMessageCustomWidth?: boolean;
  label?: string;
  mandatory?: boolean;
  name: string;
  nonWrappingLabelledPassword?: boolean;
  infotext?: string;
  reference?: any;
  srOnly?: string;
  inline?: boolean;
  customposition?: string;
}

const Input = (props: Props): React.JSX.Element => {
  const { lng } = useParams() as { lng: string };
  const { t } = doTranslation(lng);
  const {
    cardType,
    dark,
    disabled,
    error,
    errorMessageCustomWidth,
    label,
    mandatory,
    name,
    nonWrappingLabelledPassword,
    type,
    reference,
    srOnly,
    inline,
    ...rest
  } = props;
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState(error);
  const labelText = `${label ? label : ''} ${label ? (mandatory ? '*' : '') : ''}`;

  useEffect(() => {
    setLocalError(error);
  }, [error]);

  const renderVisa = () => {
    return (
      <ResizedCdnImage
        alt={t('visa')}
        aria-hidden="true"
        height={16}
        src={'/assets/images/global/purchase/VISA.svg'}
        width={24}
      />
    );
  };

  const renderMasterCard = () => {
    return (
      <ResizedCdnImage
        alt={t('Master card')}
        aria-hidden="true"
        height={16}
        src={'/assets/images/global/purchase/MASTERCARD.svg'}
        width={24}
      />
    );
  };

  const renderAmericanExpress = () => {
    return (
      <ResizedCdnImage
        alt={t('AMEX')}
        aria-hidden="true"
        height={16}
        src={'/assets/images/global/purchase/AMERICANEXPRESS.svg'}
        width={24}
      />
    );
  };

  const renderDiscover = () => {
    return (
      <ResizedCdnImage
        alt={t('Discover')}
        aria-hidden="true"
        height={16}
        src={'/assets/images/global/purchase/DISCOVER.svg'}
        width={24}
      />
    );
  };

  const renderCards = () => {
    if (cardType === 'VISA') {
      return <div className={styles.banks}>{renderVisa()}</div>;
    }
    if (cardType === 'MASTERCARD') {
      return <div className={styles.banks}>{renderMasterCard()}</div>;
    }
    if (cardType === 'AMERICANEXPRESS') {
      return <div className={styles.banks}>{renderAmericanExpress()}</div>;
    }
    if (cardType === 'DISCOVER') {
      return <div className={styles.banks}>{renderDiscover()}</div>;
    }
    return (
      <div className={styles.banks}>
        {renderVisa()}
        {renderMasterCard()}
        {renderAmericanExpress()}
        {renderDiscover()}
      </div>
    );
  };

  const renderInput = () => {
    return (
      <input
        aria-describedby={localError ? `${name}-error-message` : undefined}
        aria-disabled={disabled === true ? 'true' : undefined}
        aria-invalid={localError ? 'true' : undefined}
        aria-required={mandatory === true ? 'true' : undefined}
        className={classnames({
          [styles.input]: true,
          [styles.dark]: dark,
          [styles.error]: localError,
          [styles['spaceing']]: type === 'password' || false
        })}
        disabled={disabled}
        name={name}
        type={showPassword ? 'text' : type}
        ref={reference}
        id={props.name}
        onBlur={() => {
          if (props.value) {
            setLocalError(undefined);
          }
        }}
        {...rest}
      />
    );
  };

  const renderError = () => {
    return (
      <div
        className={classnames({
          [styles['error-message']]: true,
          [styles.dark]: dark,
          [`${name}-error-message`]: localError,
          [styles['adjust-top']]: name === 'card-number',
          [styles['error-message-custom-width']]:
            errorMessageCustomWidth !== undefined && errorMessageCustomWidth === true
        })}
      >
        <span className="visually-hidden">{localError?.screenReader}</span>
        {localError?.normal}
        <span className="visually-hidden">{t('When you have corrected the input, press enter.')}</span>
      </div>
    );
  };

  const renderNonWrappingLabelledPassword = () => {
    return (
      <div className={styles.one}>
        <div className={styles.two}>
          <label
            className={classnames({
              [styles.label]: true,
              [styles.dark]: dark,
              [styles.error]: localError
            })}
            htmlFor={props.name}
          >
            {props.infotext ? (
              // eslint-disable-next-line
              <div className={styles.info} onClick={(e) => e.preventDefault()}>
                {label ? label : ''}
                {mandatory ? '*' : ''}
              </div>
            ) : srOnly ? (
              <div>
                {label}
                <span className="sr-only">{srOnly}</span>
                {' *'}
              </div>
            ) : (
              labelText
            )}
          </label>
          {props.infotext && (
            <div className={styles.group}>
              <Tooltip
                ariaLabelClose={'Password more info'}
                ariaLabelOpen={'close Password more info'}
                content={props.infotext}
                id="info"
                place="right"
                wrap={true}
              >
                <ResizedCdnImage alt={t('info')} aria-hidden="true" height={12} src={info} width={12} />
              </Tooltip>
            </div>
          )}
        </div>
        {renderInput()}
        {renderShowHidePassword()}
        {localError && renderError()}
      </div>
    );
  };

  const renderShowHidePassword = () => {
    return (
      <div className={styles.show}>
        <Image
          alt={showPassword ? t('Hide password') : t('Show password')}
          className={styles.eye}
          height={13.222}
          onClick={(e) => {
            e.preventDefault();
            setShowPassword((prevState) => !prevState);
          }}
          onKeyDown={(e: any) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowPassword((prevState) => !prevState);
            }
          }}
          role="button"
          src={eye}
          tabIndex={0}
          width={17}
        />
      </div>
    );
  };

  return nonWrappingLabelledPassword ? (
    renderNonWrappingLabelledPassword()
  ) : (
    <label
      className={classnames({
        [styles.label]: true,
        [styles.dark]: dark,
        [styles.error]: localError,
        [styles.inline]: inline,
        [styles['width-100-percent']]: true
      })}
      htmlFor={props.name}
    >
      {type === 'password' && props.infotext ? (
        // eslint-disable-next-line
        <div className={styles.info} onClick={(e) => e.preventDefault()}>
          {label ? label : ''}
          {mandatory ? '*' : ''}
          <div className={styles.group}>
            <Tooltip
              ariaLabelClose={'Password more info'}
              ariaLabelOpen={'close Password more info'}
              content={props.infotext}
              id="info"
              place="right"
              wrap={true}
            >
              <ResizedCdnImage alt={t('info')} aria-hidden="true" height={12} src={info} width={12} />
            </Tooltip>
          </div>
        </div>
      ) : srOnly ? (
        <div>
          {label}
          <span className="sr-only">{srOnly}</span>
          {' *'}
        </div>
      ) : (
        labelText
      )}
      <input
        aria-describedby={localError ? `${name}-error-message` : undefined}
        aria-disabled={disabled === true ? 'true' : undefined}
        aria-invalid={localError ? 'true' : undefined}
        aria-required={mandatory === true ? 'true' : undefined}
        className={classnames({
          [styles.input]: true,
          [styles.dark]: dark,
          [styles.error]: localError,
          [styles['spaceing']]: type === 'password' || false
        })}
        disabled={disabled}
        name={name}
        type={showPassword ? 'text' : type}
        ref={reference}
        id={props.name}
        onBlur={() => {
          if (props.value) {
            setLocalError(undefined);
          }
        }}
        {...rest}
      />
      {type === 'password' && renderShowHidePassword()}
      {name === 'card-number' && renderCards()}
      {localError && (
        <div
          className={classnames({
            [styles['error-message']]: true,
            [styles['error-message-absolute-position']]: props.customposition === 'true',
            [styles.dark]: dark,
            [`${name}-error-message`]: localError,
            [styles['adjust-top']]: name === 'card-number',
            [styles['error-message-custom-width']]:
              errorMessageCustomWidth !== undefined && errorMessageCustomWidth === true
          })}
        >
          <span className="visually-hidden">{localError.screenReader}</span>
          {localError.normal}
          <span className="visually-hidden">{t('When you have corrected the input, press enter.')}</span>
        </div>
      )}
    </label>
  );
};

export default Input;
